import type { GenericState } from '@/store/reducers/sliceTemplate';
import type { ProgressPayload } from '@/store/types/resultTypes';

/**
 * Контракт «Мои характеристики» — единственная точка правды для полей,
 * которыми клиент обменивается с бэкендом по разделу «Я» в профиле.
 *
 * Соответствующий эндпоинт — в `src/api/apiConfig.ts`:
 *   profileMeCards → GET /api/profile/me-cards
 *
 * === Контракт ответа: GET /api/profile/me-cards ===
 *
 * ```json
 * {
 *   "data": [
 *     {
 *       "id": "personality",
 *       "front": { "title": "Интроверт", "img": "https://cdn/.../personality.svg" },
 *       "back":  { "title": "Тип личности",
 *                  "items": [
 *                    { "key": 50, "label": "Интроверт", "value": 57 },
 *                    { "key": 52, "label": "Экстраверт", "value": 43 }
 *                  ],
 *                  "show_percent": true },
 *       "variant": "bars",                 // "bars" | "circle"
 *       "circle_value": "128",             // только для variant=circle
 *       "circle_percent": 70,              // 0..100, только для variant=circle
 *       "status": "passed",                // "passed" | "in_progress" | "not_passed"
 *       "test_url": "/tests/personality"
 *     }
 *   ]
 * }
 * ```
 *
 * Бэкенд меняет имя поля или формат — правим этот файл (типы + `normalizeMeCard`),
 * остальной код (slice / selectors / компоненты) поедет автоматически.
 */

/** Статус прохождения теста, стоящего за карточкой характеристики. */
export type MeCardStatus = 'passed' | 'inProgress' | 'notPassed';

/** Wire-shape статуса с бэка (snake_case). */
export type MeCardStatusWire = 'passed' | 'in_progress' | 'not_passed';

/** Как рендерить обратную сторону карточки. */
export type MeCardVariant = 'bars' | 'circle';

/** Wire-shape ответа: `back` приходит с `show_percent` (snake) и теми же items. */
export interface MeCardBackWire {
	title?: string;
	img?: string;
	items: Array<{ key: string | number; label: string; value: number; max?: number; wrong?: number }>;
	show_percent?: boolean;
}

/** Wire-shape одной карточки. */
export interface MeCardWire {
	id: string;
	front: { title: string; img?: string };
	back?: MeCardBackWire;
	variant?: MeCardVariant;
	circle_value?: string;
	circle_percent?: number;
	status: MeCardStatusWire;
	test_url: string;
}

/**
 * UI-ready форма одной карточки — именно её хранит Redux и видят селекторы.
 * Маппинг wire→UI делает `normalizeMeCard` (см. ниже), компоненты UI-маппингом
 * не занимаются (см. `redux-normalization.mdc`).
 */
export interface MeCardData {
	id: string;
	front: { title: string; img?: string };
	back?: ProgressPayload;
	variant?: MeCardVariant;
	circleValue?: string;
	circlePercent?: number;
	status: MeCardStatus;
	testUrl: string;
}

/**
 * Полностью «разрешённая» карточка — то, что фактически рендерит
 * `AttributeFlipCard`. Edge-case логика (single-bar → circle, slice до
 * `MAX_VISIBLE_ITEMS`, дефолтные `circleTitle` / `circleValue`) живёт в
 * селекторе `selectResolvedMeCards`, а карточка получает уже готовые поля
 * для рендера — без `useMemo` / `if`-веток в компоненте.
 */
export interface MeCardResolved extends MeCardData {
	/** Эффективный вариант после edge-case'ов (`single bar` → `circle`). */
	resolvedVariant: MeCardVariant;
	/** Items с back-стороны, обрезанные до `MAX_VISIBLE_ITEMS`. */
	visibleItems: ProgressPayload['items'];
	/** Заголовок back-стороны: для `circle` — название характеристики. */
	resolvedCircleTitle: string;
	/** Большое число/строка в центре кольца. */
	resolvedCircleValue?: string;
	/** Процент заполнения кольца (0..100). */
	resolvedCirclePercent: number;
}

/** Сколько прогрессов максимум показываем на back-стороне (см. JSDoc в карточке). */
export const MAX_VISIBLE_ITEMS = 4;

/** Состояние слайса `meCards` в Redux. */
export interface MeCardsState extends GenericState<MeCardData> {}

/** Wire-статус → UI-статус. */
export const mapMeCardStatus = (s: MeCardStatusWire): MeCardStatus => {
	if (s === 'passed') return 'passed';
	if (s === 'in_progress') return 'inProgress';
	return 'notPassed';
};

/**
 * Wire-shape → UI-ready. Точка входа для всей нормализации snake→camel
 * по карточкам «Я». Меняем имя поля на бэке — правим тут.
 */
export const normalizeMeCard = (item: MeCardWire): MeCardData => ({
	id: item.id,
	// `front` — инвариант контракта, но битая/неполная карточка с бэка не
	// должна ронять всю секцию «Я» (раньше item.front.title бросал TypeError,
	// который ловился catch'ем как «Error (null)»). Подстраховываемся.
	front: { title: item.front?.title ?? '', img: item.front?.img },
	back: item.back
		? {
			title: item.back.title,
			img: item.back.img,
			items: item.back.items,
			showPercent: item.back.show_percent,
		}
		: undefined,
	variant: item.variant,
	circleValue: item.circle_value,
	circlePercent: item.circle_percent,
	status: mapMeCardStatus(item.status),
	testUrl: item.test_url,
});
